// @ts-nocheck -- staged migration: add explicit types before removing this directive.
// Conjugates verbs classified by JMdict's part-of-speech tags (v1 = ichidan,
// v5* = godan by row, vs*/vk = irregular suru/kuru). Godan row data below
// follows standard textbook rules; the documented exceptions (行く-style く verbs,
// 問う-style う verbs, ある's irregular negative, ずる verbs' じ stem) are special-cased.

const GODAN_ROWS = {
    u: { a: "わ", i: "い", e: "え", o: "お" },
    k: { a: "か", i: "き", e: "け", o: "こ" },
    g: { a: "が", i: "ぎ", e: "げ", o: "ご" },
    s: { a: "さ", i: "し", e: "せ", o: "そ" },
    t: { a: "た", i: "ち", e: "て", o: "と" },
    n: { a: "な", i: "に", e: "ね", o: "の" },
    b: { a: "ば", i: "び", e: "べ", o: "ぼ" },
    m: { a: "ま", i: "み", e: "め", o: "も" },
    r: { a: "ら", i: "り", e: "れ", o: "ろ" },
};

const GODAN_TE_TA = {
    u: { te: "って", ta: "った" },
    k: { te: "いて", ta: "いた" },
    g: { te: "いで", ta: "いだ" },
    s: { te: "して", ta: "した" },
    t: { te: "って", ta: "った" },
    n: { te: "んで", ta: "んだ" },
    b: { te: "んで", ta: "んだ" },
    m: { te: "んで", ta: "んだ" },
    r: { te: "って", ta: "った" },
};

// v5k-s (行く/往く and compounds) uses う-row-style sound changes, not く's.
const IKU_TE_TA = { te: "って", ta: "った" };

// v5u-s (問う, 乞う, 恋う): the う verbs of JMdict's "special class" keep the
// vowel instead of doubling the consonant: 問うて／問うた, not 問って／問った.
const U_SPECIAL_TE_TA = { te: "うて", ta: "うた" };

// Shared across all four conjugation classes (and re-used by conjugationRomaji.ts
// to apply the ません／でした word-boundary fix) so the label text only lives once.
export const POLITE_PAST_LABEL = "Polite past (ました)";
export const POLITE_NEGATIVE_PAST_LABEL = "Polite negative past (ませんでした)";

// The one te-form label every conjugation class emits. The ている forms
// (teIruConjugation.ts) are built by looking this label up in conjugateVerb's
// own output, so the te-form and ている can never be derived two different ways.
export const TE_FORM_LABEL = "Te form";

function rowFromVerbType(verbType) {
    // "v5u", "v5u-s", "v5uru", "v5aru" -> "u"; "v5r-i" -> "r"; etc.
    const match = /^v5([a-z])/.exec(verbType || "");
    return match ? match[1] : null;
}

// The te/ta endings for a godan verb: the row's regular sound change, except
// for the two JMdict classes whose te-form is irregular.
function teTaFor(verbType, row) {
    if (verbType === "v5k-s") return IKU_TE_TA;
    if (verbType === "v5u-s") return U_SPECIAL_TE_TA;
    return GODAN_TE_TA[row];
}

function godanConjugations(dictionaryForm, reading, verbType) {
    const isIkuStyle = verbType === "v5k-s";
    const row = isIkuStyle ? "k" : rowFromVerbType(verbType);
    if (!row || !GODAN_ROWS[row]) return null;

    const stem = dictionaryForm.slice(0, -1);
    const readingStem = reading.slice(0, -1);
    const { a, i, e, o } = GODAN_ROWS[row];
    const { te, ta } = teTaFor(verbType, row);

    // ある (and its irregular v5r-i siblings) drop to plain ない rather than
    // the regular あらない the row rules would otherwise produce.
    const isAruIrregular = verbType === "v5r-i" && reading.endsWith("ある");
    const naiBase = isAruIrregular ? dictionaryForm.slice(0, -2) : stem + a;
    const naiReadingBase = isAruIrregular ? readingStem.slice(0, -1) : readingStem + a;

    return [
        { label: "Dictionary form", kanji: dictionaryForm, reading },
        { label: "Polite (ます)", kanji: stem + i + "ます", reading: readingStem + i + "ます" },
        { label: "Polite negative", kanji: stem + i + "ません", reading: readingStem + i + "ません" },
        { label: "Negative (ない)", kanji: naiBase + "ない", reading: naiReadingBase + "ない" },
        { label: "Casual past (た)", kanji: stem + ta, reading: readingStem + ta },
        { label: "Casual negative past (なかった)", kanji: naiBase + "なかった", reading: naiReadingBase + "なかった" },
        { label: POLITE_PAST_LABEL, kanji: stem + i + "ました", reading: readingStem + i + "ました" },
        {
            label: POLITE_NEGATIVE_PAST_LABEL,
            kanji: stem + i + "ませんでした",
            reading: readingStem + i + "ませんでした",
        },
        { label: TE_FORM_LABEL, kanji: stem + te, reading: readingStem + te },
        { label: "Potential", kanji: stem + e + "る", reading: readingStem + e + "る" },
        { label: "Volitional (よう)", kanji: stem + o + "う", reading: readingStem + o + "う" },
        { label: "Conditional (ば)", kanji: stem + e + "ば", reading: readingStem + e + "ば" },
        { label: "Imperative", kanji: stem + e, reading: readingStem + e },
    ];
}

function ichidanConjugations(dictionaryForm, reading, verbType) {
    const stem = dictionaryForm.slice(0, -1);
    const readingStem = reading.slice(0, -1);
    // ずる verbs (信ずる, 論ずる, ...) are the older spelling of じる verbs (JMdict
    // "vz", every one ends in ずる): only the dictionary form and the conditional
    // keep the ず; every other inflection takes じ (信じて, 信じない, 信じます),
    // so dropping just the る would give the non-word ×信ずて.
    const isZuru = verbType === "vz";
    const inflectedStem = isZuru ? stem.slice(0, -1) + "じ" : stem;
    const inflectedReadingStem = isZuru ? readingStem.slice(0, -1) + "じ" : readingStem;

    return [
        { label: "Dictionary form", kanji: dictionaryForm, reading },
        { label: "Polite (ます)", kanji: inflectedStem + "ます", reading: inflectedReadingStem + "ます" },
        { label: "Polite negative", kanji: inflectedStem + "ません", reading: inflectedReadingStem + "ません" },
        { label: "Negative (ない)", kanji: inflectedStem + "ない", reading: inflectedReadingStem + "ない" },
        { label: "Casual past (た)", kanji: inflectedStem + "た", reading: inflectedReadingStem + "た" },
        {
            label: "Casual negative past (なかった)",
            kanji: inflectedStem + "なかった",
            reading: inflectedReadingStem + "なかった",
        },
        { label: POLITE_PAST_LABEL, kanji: inflectedStem + "ました", reading: inflectedReadingStem + "ました" },
        {
            label: POLITE_NEGATIVE_PAST_LABEL,
            kanji: inflectedStem + "ませんでした",
            reading: inflectedReadingStem + "ませんでした",
        },
        { label: TE_FORM_LABEL, kanji: inflectedStem + "て", reading: inflectedReadingStem + "て" },
        { label: "Potential", kanji: inflectedStem + "られる", reading: inflectedReadingStem + "られる" },
        { label: "Volitional (よう)", kanji: inflectedStem + "よう", reading: inflectedReadingStem + "よう" },
        { label: "Conditional (ば)", kanji: stem + "れば", reading: readingStem + "れば" },
        { label: "Imperative", kanji: inflectedStem + "ろ", reading: inflectedReadingStem + "ろ" },
    ];
}

// する compounds (勉強する, ...) conjugate on the する tail only. A plain "vs"
// tag (意味, 料理, ...) marks a noun that TAKES suru without する already in
// its headword, so append it before conjugating.
function suruConjugations(dictionaryForm, reading) {
    const hasSuru = dictionaryForm.endsWith("する") || dictionaryForm.endsWith("為る");
    const base = hasSuru ? dictionaryForm : dictionaryForm + "する";
    const baseReading = hasSuru ? reading : reading + "する";
    const prefix = base.slice(0, -2);
    const readingPrefix = baseReading.slice(0, -2);

    const tail = [
        { label: "Dictionary form", kanji: "する", reading: "する" },
        { label: "Polite (ます)", kanji: "します", reading: "します" },
        { label: "Polite negative", kanji: "しません", reading: "しません" },
        { label: "Negative (ない)", kanji: "しない", reading: "しない" },
        { label: "Casual past (た)", kanji: "した", reading: "した" },
        { label: "Casual negative past (なかった)", kanji: "しなかった", reading: "しなかった" },
        { label: POLITE_PAST_LABEL, kanji: "しました", reading: "しました" },
        { label: POLITE_NEGATIVE_PAST_LABEL, kanji: "しませんでした", reading: "しませんでした" },
        { label: TE_FORM_LABEL, kanji: "して", reading: "して" },
        { label: "Potential", kanji: "できる", reading: "できる" },
        { label: "Volitional (よう)", kanji: "しよう", reading: "しよう" },
        { label: "Conditional (ば)", kanji: "すれば", reading: "すれば" },
        { label: "Imperative", kanji: "しろ", reading: "しろ" },
    ];

    return tail.map((form) => ({
        label: form.label,
        kanji: prefix + form.kanji,
        reading: readingPrefix + form.reading,
    }));
}

// 来る compounds (持って来る, 帰って来る, ...) conjugate on the 来る tail,
// which changes its reading (来 -> こ/き/く/こ) but not its kanji.
function kuruConjugations(dictionaryForm, reading) {
    const kanjiPrefix = dictionaryForm.endsWith("来る") ? dictionaryForm.slice(0, -2) : "";
    const readingPrefix = reading.endsWith("くる") ? reading.slice(0, -2) : "";

    const tail = [
        { label: "Dictionary form", kanji: "来る", reading: "くる" },
        { label: "Polite (ます)", kanji: "来ます", reading: "きます" },
        { label: "Polite negative", kanji: "来ません", reading: "きません" },
        { label: "Negative (ない)", kanji: "来ない", reading: "こない" },
        { label: "Casual past (た)", kanji: "来た", reading: "きた" },
        { label: "Casual negative past (なかった)", kanji: "来なかった", reading: "こなかった" },
        { label: POLITE_PAST_LABEL, kanji: "来ました", reading: "きました" },
        { label: POLITE_NEGATIVE_PAST_LABEL, kanji: "来ませんでした", reading: "きませんでした" },
        { label: TE_FORM_LABEL, kanji: "来て", reading: "きて" },
        { label: "Potential", kanji: "来られる", reading: "こられる" },
        { label: "Volitional (よう)", kanji: "来よう", reading: "こよう" },
        { label: "Conditional (ば)", kanji: "来れば", reading: "くれば" },
        { label: "Imperative", kanji: "来い", reading: "こい" },
    ];

    return tail.map((form) => ({
        label: form.label,
        kanji: kanjiPrefix + form.kanji,
        reading: readingPrefix + form.reading,
    }));
}

const SURU_TAGS = new Set(["vs", "vs-i", "vs-c", "vs-s"]);

export const MASHOUKA_LABEL = "〜ましょうか — Offer / suggestion";
export const MASHOUKA_SUFFIX = "ましょうか";

function withMashouka(forms) {
    const polite = forms.find((form) => form.label === "Polite (ます)");
    const mashouka = {
        label: MASHOUKA_LABEL,
        kanji: polite.kanji.slice(0, -2) + MASHOUKA_SUFFIX,
        reading: polite.reading.slice(0, -2) + MASHOUKA_SUFFIX,
    };
    const volitionalIndex = forms.findIndex((form) => form.label === "Volitional (よう)");
    return [...forms.slice(0, volitionalIndex + 1), mashouka, ...forms.slice(volitionalIndex + 1)];
}

export const TO_OMOIMASU_LABEL = "〜と思います — I think that…";
export const TO_OMOIMASU_KANJI_SUFFIX = "と思います";
export const TO_OMOIMASU_READING_SUFFIX = "とおもいます";

// 〜と思います ("I think that ...") follows the PLAIN form with no stem change,
// so unlike ましょうか it is built from the dictionary form, for every verb
// type. It sits right after ましょうか: both are phrase patterns rather than
// stand-alone inflections, so they stay together ahead of the conditional.
function withToOmoimasu(forms) {
    const dictionary = forms.find((form) => form.label === "Dictionary form");
    const toOmoimasu = {
        label: TO_OMOIMASU_LABEL,
        kanji: dictionary.kanji + TO_OMOIMASU_KANJI_SUFFIX,
        reading: dictionary.reading + TO_OMOIMASU_READING_SUFFIX,
    };
    const mashoukaIndex = forms.findIndex((form) => form.label === MASHOUKA_LABEL);
    return [...forms.slice(0, mashoukaIndex + 1), toOmoimasu, ...forms.slice(mashoukaIndex + 1)];
}

// The single source of truth for "what traditional group is this verb" -
// Godan (Group 1), Ichidan (Group 2) or Irregular (Group 3, suru/kuru) -
// built from the exact same JMdict tag checks baseConjugations uses to pick
// a conjugation branch below. Shared with the Verb Conjugation page's POS
// badge and its group filter so neither can drift out of sync with this.
// Returns null for a tag this engine doesn't classify into any of the three
// (e.g. an obsolete/archaic JMdict class like "v2"/"v4").
export function verbGroupOf(verbType) {
    if (!verbType) return null;
    if (verbType === "vk" || SURU_TAGS.has(verbType)) return "irregular";
    if (verbType === "v1" || verbType === "v1-s" || verbType === "vz") return "ichidan";
    if (verbType.startsWith("v5")) return "godan";
    return null;
}

function baseConjugations(word, reading, verbType) {
    const group = verbGroupOf(verbType);
    if (group === "irregular") return verbType === "vk" ? kuruConjugations(word, reading) : suruConjugations(word, reading);
    if (group === "ichidan") return ichidanConjugations(word, reading, verbType);
    if (group === "godan") return godanConjugations(word, reading, verbType);

    return null;
}

// Returns an ordered list of { label, kanji, reading } conjugated forms for a
// verb, or null if verbType isn't a conjugation class this engine covers.
export function conjugateVerb(word, reading, verbType) {
    if (!word || !reading || !verbType) return null;

    const forms = baseConjugations(word, reading, verbType);
    return forms ? withToOmoimasu(withMashouka(forms)) : null;
}
